// Verzendmoment op de productpagina, naast "Op voorraad".
// Op een werkdag voor 21:00: "Vandaag verstuurd. Bestel binnen x u y min".
// Daarna, in het weekend en op feestdagen: "Verstuurd op <eerstvolgende werkdag>".
// Feestdagen komen van date.nager.at, voor dit jaar en volgend jaar, zodat ook
// de jaarwisseling klopt. Er hoeft dus nooit een jaartal bijgewerkt te worden.
class EstimatedShipping extends HTMLElement {
  constructor() {
    super();
    this.cutoffHour = 21;
    this.cutoffMinutes = 0;
    this.holidays = [];
    this.updateInterval = null;
  }

  connectedCallback() {
    this.shippingDatePdpElement = this.querySelector('.shipping-date-pdp');
    // Alleen aanwezig bij voorraad en bezoekers uit NL.
    if (!this.shippingDatePdpElement) return;
    this.init();
  }

  disconnectedCallback() {
    if (this.updateInterval) clearInterval(this.updateInterval);
  }

  async init() {
    this.holidays = await this.getHolidays();
    this.calculateShipping();
    this.updateInterval = setInterval(() => this.calculateShipping(), 60000);
  }

  async getHolidays() {
    const year = new Date().getFullYear();
    try {
      const lists = await Promise.all(
        [year, year + 1].map((y) =>
          fetch(`https://date.nager.at/api/v3/publicholidays/${y}/NL`).then((r) => (r.ok ? r.json() : []))
        )
      );
      return lists
        .flat()
        .filter((holiday) => this.isClosed(holiday))
        .map((holiday) => holiday.date);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      return [];
    }
  }

  // Op Goede Vrijdag wordt gewoon verstuurd. Op Bevrijdingsdag ook, behalve in
  // de lustrumjaren (2025, 2030, 2035, ...), want dan is het een vrije dag.
  isClosed(holiday) {
    if (holiday.name === 'Good Friday') return false;
    if (holiday.name === 'Liberation Day') return Number(holiday.date.slice(0, 4)) % 5 === 0;
    return true;
  }

  // Lokale datum als jjjj-mm-dd. Niet toISOString, want die rekent in UTC
  // en geeft tussen 00:00 en 02:00 de datum van gisteren.
  dateKey(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  isWorkday(date) {
    const day = date.getDay();
    return day !== 0 && day !== 6 && !this.holidays.includes(this.dateKey(date));
  }

  calculateShipping() {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const pastCutoff = hours > this.cutoffHour || (hours === this.cutoffHour && minutes >= this.cutoffMinutes);

    if (this.isWorkday(now) && !pastCutoff) {
      const minutesLeft = (this.cutoffHour - hours) * 60 + (this.cutoffMinutes - minutes);
      this.displayShippingMessage('Vandaag verstuurd', minutesLeft);
      return;
    }

    const next = new Date(now);
    do {
      next.setDate(next.getDate() + 1);
    } while (!this.isWorkday(next));

    const label = next.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
    this.displayShippingMessage(`Verstuurd op ${label}`, 0);
  }

  displayShippingMessage(message, minutesLeft) {
    if (minutesLeft > 0) {
      const h = Math.floor(minutesLeft / 60);
      const m = minutesLeft % 60;
      this.shippingDatePdpElement.innerHTML = `${message}. Bestel binnen ${h} u ${m} min`;
    } else {
      this.shippingDatePdpElement.innerHTML = message;
    }
  }
}

if (customElements.get('estimated-shipping') === undefined) {
  customElements.define('estimated-shipping', EstimatedShipping);
}
